// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A timeline segment component for asset assignments.
 * This module provides a visual representation of an asset's
 * assignment period in a timeline, with support for project
 * intervals, future dates, and interactive editing. It handles
 * complex date calculations and visual states.
 *
 * Key features:
 * - Timeline visualization
 * - Project intervals
 * - Future date handling
 * - Interactive editing
 * - Visual states
 * - Mouse interactions
 */
import { AssetProjectHistorySegment } from '@/asset/api/get-asset-project-history';
import { Asset } from '@/asset/api/types';
import { UpdateAssetAssignmentDialog } from '@/asset/components/update-asset-assignment-dialog';
import { useTimeline } from '@/contexts/timeline-context';
import { useDisclosure } from '@/hooks/use-disclosure';
import { getInterval } from '@/project/utilities/get-interval';
import { Box, Typography } from '@mui/material';
import { AxisScale } from '@visx/axis';
import { BarRounded } from '@visx/shape';
import { DateTime, Interval } from 'luxon';
import { MouseEvent, MouseEventHandler } from 'react';

/**
 * Gets the maximum date from a scale's domain.
 * Converts the scale's end date to a DateTime object.
 *
 * @param scale - The axis scale to extract the date from
 * @returns The maximum date as DateTime or null
 */
function getMaximumDate(scale: AxisScale<number>) {
  const [_, end] = scale.domain();

  if (end instanceof Date) {
    return DateTime.fromJSDate(end);
  }

  return null;
}

/**
 * Splits an interval into segments based on project dates.
 * Creates sub-intervals for different visual states like
 * past/future and project/non-project periods.
 *
 * @param segmentInterval - The full segment interval
 * @param projectInterval - Optional project interval
 * @param today - Current date for past/future split
 * @returns Array of sub-intervals
 */
function getIntervals(
  segmentInterval: Interval,
  projectInterval: Interval | null,
  today: DateTime
) {
  if (projectInterval === null) {
    return segmentInterval.splitAt(today);
  }

  return segmentInterval.splitAt(
    today,
    projectInterval.start,
    projectInterval.end
  );
}

/**
 * Props for configuring the AssetAssignmentTimelineSegment component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset associated with the segment.
   * Only key and serial are required.
   */
  asset: Pick<Asset, 'key' | 'serial'>;

  /**
   * Vertical position (y coordinate) of the segment.
   * Used for layout in the timeline.
   */
  y: number;

  /**
   * Height of the segment bar.
   * Controls the visual size.
   */
  height: number;

  /**
   * Time scale for the timeline.
   * Used to convert dates to positions.
   */
  scale: AxisScale<number>;

  /**
   * The segment data to display.
   * Contains project and date information.
   */
  segment: AssetProjectHistorySegment;

  /**
   * Whether the segment can be edited.
   * Enables click-to-edit behavior.
   */
  isEditable?: boolean;

  /**
   * Whether the segment is invalid.
   * Changes visual appearance.
   */
  isInvalid?: boolean;

  /**
   * Click event handler.
   * Called when segment is clicked.
   */
  onClick?: MouseEventHandler<SVGElement>;

  /**
   * Mouse move event handler.
   * Called when mouse moves over segment.
   */
  onMouseMove?: MouseEventHandler<SVGElement>;

  /**
   * Mouse leave event handler.
   * Called when mouse leaves segment.
   */
  onMouseLeave?: MouseEventHandler<SVGElement>;
}

/**
 * A timeline segment component for asset assignments.
 * Visualizes an assignment period with different states for
 * project intervals, future dates, and invalid states. Supports
 * interactive editing and mouse interactions.
 *
 * Features:
 * - Timeline visualization
 * - Project interval display
 * - Future date handling
 * - Interactive editing
 * - Visual states
 * - Mouse interactions
 * - Responsive layout
 *
 * Usage example:
 * ```tsx
 * function AssignmentTimeline({ asset, segment }: Props) {
 *   return (
 *     <AssetAssignmentTimelineSegment
 *       asset={asset}
 *       segment={segment}
 *       scale={dateScale}
 *       y={0}
 *       height={30}
 *       isEditable
 *     />
 *   );
 * }
 * ```
 */
export function AssetAssignmentTimelineSegment(props: Props) {
  const dialog = useDisclosure();
  const { palette } = useTimeline();

  // Calculate dates and intervals
  const today = DateTime.now().startOf('day');
  const maximumDate = getMaximumDate(props.scale);
  const segmentInterval = Interval.fromDateTimes(
    props.segment.start,
    DateTime.max(props.segment.end ?? maximumDate ?? today, props.segment.start)
  );

  const projectInterval = getInterval(props.segment.project);
  const intervals = getIntervals(segmentInterval, projectInterval, today);
  const cursor = props.isEditable ? 'pointer' : 'default';

  // Calculate text position and width
  const textX = props.scale(props.segment.start) ?? 0;
  const segmentEnd = props.segment.end ?? maximumDate ?? today;
  const textWidth = (props.scale(segmentEnd) ?? 0) - textX;

  /**
   * Handles segment click events.
   * Opens edit dialog if segment is editable.
   */
  const handleClick = (event: MouseEvent<SVGElement>) => {
    if (props.onClick) {
      props.onClick(event);
    }

    if (props.isEditable) {
      dialog.open();
    }
  };

  return (
    <>
      <UpdateAssetAssignmentDialog
        asset={props.asset}
        assignment={props.segment}
        isOpen={dialog.isOpen}
        onClose={dialog.close}
      />
      <g
        onClick={handleClick}
        onMouseMove={props.onMouseMove}
        onMouseLeave={props.onMouseLeave}
        style={{ cursor }}
      >
        {intervals.map((interval, index) => {
          const key = interval.start.toISO();
          const x = props.scale(interval.start) ?? 0;
          const width = (props.scale(interval.end) ?? 0) - x;
          const left = index === 0;
          const right = index === intervals.length - 1;
          const isFuture = interval.start >= today;
          const isProject = projectInterval?.contains(interval.start) ?? false;

          const stroke = props.isInvalid ? palette.invalid.solid : undefined;
          const stokeWidth = props.isInvalid ? 2 : undefined;

          // Determine fill color based on state
          let fill = isFuture
            ? palette.assignment.diagonal
            : palette.assignment.solid;

          if (isProject) {
            fill = isFuture ? palette.project.diagonal : palette.project.solid;
          }

          return (
            <BarRounded
              key={key}
              x={x}
              y={props.y}
              fill={fill}
              left={left}
              right={right}
              width={width}
              height={props.height}
              radius={4}
              stroke={stroke}
              strokeWidth={stokeWidth}
            />
          );
        })}
        <foreignObject
          x={textX}
          y={props.y}
          height={props.height}
          width={textWidth}
        >
          <Box paddingX={1} height="100%" display="flex" alignItems="center">
            <Typography color="white" variant="body2" whiteSpace="nowrap">
              {props.segment.project.name}
            </Typography>
          </Box>
        </foreignObject>
      </g>
    </>
  );
}
